/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import {
  SortOrder,
  PastActionsForResourceSortFields,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { getNoSearchResultsMessage } from "~/components/SearchInput";
import { TableWrapper } from "~/components/Tables";
import { useQueryState, useGetSearchPhrase } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { getDisplayNameByActionType } from "~/views/plans/utils";
import { useGetPastActionsForResource } from "../../hooks/useGetPastActionsForResource/useGetPastActionsForResource";
import {
  ResourceDetailsActionsQuery,
  PastActionForResourceWithResourceId,
} from "../../types/ResourceDetailsActionsQuery";
import { preparePastActionsFilter } from "./resourceDetailsActionFilterBuilders";
import { columns } from "./ResourceDetailsActionsTableColumns";

type Props = {
  resourceId: string;
  onCountChange?: (count: number) => void;
};

export function ResourceDetailsActionsTable({
  resourceId,
  onCountChange,
}: Props) {
  const { queryState } = useQueryState<ResourceDetailsActionsQuery>();
  const { phrase, isSearchAllowed } = useGetSearchPhrase(
    queryState.searchPhrase
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const {
    data: actions,
    loading,
    count,
  } = useGetPastActionsForResource({
    resourceId: resourceId ?? "",
    page: queryState.page ?? "1",
    limit: queryState.limit ?? "20",
    sort: {
      field: queryState.field ?? PastActionsForResourceSortFields.EndTime,
      order: queryState.order ?? SortOrder.Desc,
    },
    filter: preparePastActionsFilter(queryState, phrase),
  });
  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  const actionsWithResourceId = actions.map((action) => ({
    ...action,
    resourceId,
    actionName: getDisplayNameByActionType(
      action.actionName,
      action.actionType
    ),
  }));

  return (
    <>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(actionsWithResourceId)}>
              <Then>
                <NoData
                  hasIcon
                  message={"There are no Actions with this Resource."}
                />
              </Then>
              <Else>
                <>
                  <TableWrapper<Partial<PastActionForResourceWithResourceId>>
                    loading={loading}
                    data={actionsWithResourceId.filter(isNotNil)}
                    columns={columns()}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    stripped
                  />
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
